/**
 * Plan 261 §A — what a verified person's memberships mean for connecting.
 *
 * Pure, so the decision can be tested on its own. The three outcomes are the
 * ones that need different actions, and collapsing any two of them is how a
 * brand-new customer ends up staring at an empty organization selector:
 *
 *  - `create`  — no membership and no invitation waiting. Offer to make a
 *                workspace, with explicit confirmation. Never silently.
 *  - `invited` — an invitation is pending. Offer the existing acceptance
 *                journey FIRST; creating a second workspace for someone who
 *                was invited to one is the wrong answer, not a fallback.
 *  - `proceed` — one or more accepted memberships. Hand identity over and let
 *                the consent screen do the choosing, exactly as today.
 */
export type ConnectDecision = 'create' | 'invited' | 'proceed'

interface MembershipLike {
   organizationId?: string
   /**
    * WHERE THE STATUS ACTUALLY LIVES.
    *
    * `organizationUserResource.transform` nests it:
    * `{ organizationId, data: { invitationStatus, ... } }`. Reading it from
    * the top level found `undefined` on every real response, which is not
    * `'pending'`, so a person waiting on an invitation was offered a brand-new
    * workspace instead. The flat spelling is kept as a fallback because some
    * of Forward Flow's user payloads do flatten it, and a reader that accepts
    * both cannot be wrong about which one arrived.
    */
   data?: { invitationStatus?: string | null } | null
   invitationStatus?: string | null
}

const statusOf = (m: MembershipLike): string | null =>
   m?.data?.invitationStatus ?? m?.invitationStatus ?? null

const accepted = (m: MembershipLike) => {
   const status = statusOf(m)
   return (
      Boolean(m?.organizationId) && status !== 'pending' && status !== 'rejected'
   )
}

const pending = (m: MembershipLike) => statusOf(m) === 'pending'

export const decideConnect = (user: {
   userOrganizations?: MembershipLike[] | null
   invitations?: MembershipLike[] | null
}): ConnectDecision => {
   const memberships = user?.userOrganizations ?? []
   if (memberships.some(accepted)) return 'proceed'
   const waiting =
      memberships.some(pending) || (user?.invitations ?? []).some(pending)
   return waiting ? 'invited' : 'create'
}
